// IMPORT React
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// IMPORT CSS
import "../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../components/CustomFields/TextField";

// IMPORT SERVICE
import { AlertType } from "../../services/models/AlertType";
import { EncryptDecryptService } from "../../services/common/EncryptionService";
import { PreferencesContext } from "../../PreferenceContext";
import CustomBreadcrumb from "../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import { PAGE_SIZE } from "../../services/common/const";
import {
  CreateVaccineGroup,
  GetVaccineGroupDetail,
  UpdateVaccineGroup,
} from "../../services/VaccineGroupService";

// SCHEMA FOR OPTION
interface selectOption {
  label: string;
  value: string;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  vaccineGroupId: string;
  consumerId: string;
  orgId: string;
  branchId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  mode: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  vaccineGroupId: "",
  consumerId: "",
  orgId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  mode: "DROPDOWN",
};

// SCHEMA FOR FORM
interface formInitialValueTypes {
  vaccineGroupId: number;
  consumerId: number;
  orgId: number;
  branchId: number;
  vaccineGroupName: string;
  isDeleted: boolean;
}

export default function VaccineGroupSave(Props: any) {
  const { isForPopUp = false, handleClosePopUp, name } = Props;
  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // DEFAULT SCHEMA VALUE FOR FORM
  const initVaccineGroupData: formInitialValueTypes = {
    vaccineGroupId: 0,
    consumerId: 0,
    branchId: 0,
    orgId: 0,
    vaccineGroupName: isForPopUp ? name || "" : "",
    isDeleted: false,
  };

  // GETTING PARAMS
  let urlParams = useQuery();
  let curRawVaccineGroupId = urlParams.get("q");
  let vaccineGroupId = 0;
  if (curRawVaccineGroupId !== null && curRawVaccineGroupId !== undefined) {
    let decId = edService.decrypt(curRawVaccineGroupId);
    if (decId !== "" && decId !== null && decId !== undefined) {
      vaccineGroupId = +decId;
    }
  } else {
    // if (urlParams.size > 0) {
    //   navigate("/not-found");
    // }
  }

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    vaccineGroupId: Yup.number().nullable(),
    consumerId: Yup.number().required(),
    branchId: Yup.number().required(),
    orgId: Yup.number().required(),
    vaccineGroupName: Yup.string().required("Vaccine group name is required"),
    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initVaccineGroupData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  // GET VACCINE AGE GROUP
  const getVaccineGroupDetail = async () => {
    let item: any;
    let params = new URLSearchParams();
    params.append("vaccineGroupId", vaccineGroupId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());

    try {
      const res: any = await GetVaccineGroupDetail(params);
      item = res.data.data;
      methods.reset({
        ...initVaccineGroupData,
        vaccineGroupId: item?.vaccineGroupId || 0,
        vaccineGroupName: item?.name,
      });
      return item;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE VACCINE AGE GROUP
  const saveVaccineGroup = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";
      let formData = {
        vaccineGroupId: value.vaccineGroupId,
        consumerId: userContext?.consumerId,
        orgId: userContext?.organizationId,
        branchId: userContext?.branchId,
        name: value.vaccineGroupName?.trim(),
        isDeleted: value.isDeleted,
        dataManagedBy: userContext.loginDetail.userId,
      };
      if (vaccineGroupId > 0) {
        result = await UpdateVaccineGroup(formData);
      } else {
        result = await CreateVaccineGroup(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          if (result?.data) {
            handleClosePopUp("VACCINE_GROUP_CREATE", {
              vaccineGroupId: result?.data?.vaccineGroupId ?? 0,
              vaccineGroupName: value.vaccineGroupName?.trim(),
            });
          }
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveVaccineGroup(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (!isForPopUp) {
      navigate(-1);
    } else {
      handleClosePopUp("CLOSE");
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      if (vaccineGroupId > 0) {
        const item = await getVaccineGroupDetail();
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [vaccineGroupId]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box>
      <Box className={"main-container"}>
        {!isForPopUp && (
          <Grid container className="page-header">
            <Grid item xs={6} sm={6} md={6}>
              <Box
                sx={{
                  display: "flex",
                  height: "100%",
                  alignItems: "center",
                }}
              >
                <CustomBreadcrumb />
              </Box>
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="outlined"
                  type="button"
                  className="secondary-button"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                >
                  Back
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} className="group-container">
                <Grid item container xs={12} spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.vaccineGroupName
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Vaccine Group Name *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder={"Vaccine Group Name"}
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          id="vaccineGroupName"
                          size="small"
                          autoFocus
                          {...methods.register("vaccineGroupName", {
                            onChange: (e) => {
                              const upperValue = e.target.value.toUpperCase();
                              e.target.value = upperValue;
                              methods.setValue("vaccineGroupName", upperValue, {
                                shouldValidate: true,
                              });
                            },
                          })}
                          error={!!methods.formState.errors.vaccineGroupName}
                          className={`text-input ${
                            methods.formState.errors.vaccineGroupName
                              ? "error"
                              : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.vaccineGroupName?.message}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={showSkeleton || isLoading}
                  >
                    {vaccineGroupId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
}
